import React, { useEffect, useState } from "react";
import {
  getPermissionSettingsService,
  updatePermissionSettingsService,
} from "../../../Services/ApiServices";
import { AxiosResponse } from "axios";
import {
  PermissionsListDataProps,
  PermissionsSettingsResponseProps,
} from "../../../@types/api_response";
import { UpdateSettingsRequestDataProps } from "../../../@types/api_request";
import ExportOtpVerify from "../../../Modal/ExportOtpVerify";
import { Confirmation } from "../../../Modal/Confirmation";
import Loader from "../../../SharedComponents/Loader/Loader";
import toast from "react-hot-toast";
import {
  DELIVERY_TYPE,
  IS_ACCESS,
  IS_AUTO_INVOICE,
} from "../../../Utility/enums.ts";
import Styles from "./Settings.module.css";

const DELIVERY_TYPE_OPTIONS: { label: string; value: DELIVERY_TYPE }[] = [
  { label: "Door Delivery", value: DELIVERY_TYPE.Door_Delivery },
  { label: "Customer Pickup", value: DELIVERY_TYPE.Customer_Pickup },
  { label: "Both", value: DELIVERY_TYPE.Both },
];

export default function Settings() {
  const [isLoading, setisLoading] = useState<boolean>(true);
  const [settingsData, setsettingsData] =
    useState<PermissionsSettingsResponseProps | null>(null);
  const [errorText, seterrorText] = useState<string>("");
  const [isOpenOtpPopup, setisOpenOtpPopup] = useState<boolean>(false);
  const [isOpenConfirmModal, setisOpenConfirmModal] = useState<boolean>(false);

  const [permissions, setPermissions] = useState<PermissionsListDataProps[]>(
    [],
  );
  const [autoInvoiceEnabled, setAutoInvoiceEnabled] = useState<boolean>(false);
  const [isBardCodeEnabled, setisBardCodeEnabled] = useState<boolean>(false);
  const [isDealerCodeEnabled, setisDealerCodeEnabled] =
    useState<boolean>(false);
  const [prefix, setPrefix] = useState<string>("");
  const [autoInvoiceNumber, setAutoInvoiceNumber] = useState<string>("");
  const [deliveryType, setDeliveryType] = useState<DELIVERY_TYPE>(
    DELIVERY_TYPE.Door_Delivery,
  );

  useEffect(() => {
    getSettingsApiCall();
  }, []);

  useEffect(() => {
    if (settingsData) {
      setPermissions(settingsData.permissions || []);
      setAutoInvoiceEnabled(
        settingsData.settings?.is_enabled === IS_AUTO_INVOICE.enable,
      );
      setisBardCodeEnabled(
        settingsData.settings?.is_barcode_enabled === IS_ACCESS.Yes,
      );
      setisDealerCodeEnabled(
        settingsData.settings?.is_dealer_code_enabled === IS_ACCESS.Yes,
      );
      setPrefix(settingsData.settings?.prefix || "");
      setAutoInvoiceNumber(
        settingsData.settings?.auto_invoice
          ? String(settingsData.settings.auto_invoice)
          : "",
      );
      setDeliveryType(
        Number(settingsData.settings?.delivery_type) ||
        DELIVERY_TYPE.Door_Delivery,
      );
    }
  }, [settingsData]);

  const getSettingsApiCall = () => {
    setisLoading(true);
    getPermissionSettingsService()
      .then((res: AxiosResponse<PermissionsSettingsResponseProps>) => {
        seterrorText("");
        setsettingsData(res.data);
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.detail?.[0]?.msg || err.message;
        seterrorText(errMsg);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  const saveSettingsApiCall = (resetKey: string) => {
    setisLoading(true);
    const data: UpdateSettingsRequestDataProps = {
      reset_key: resetKey,
      data: permissions.flatMap((menu) =>
        menu.sub_menu.map((sub) => ({
          sub_menu_id: sub.sub_menu_id as unknown as 0,
          access: sub.access,
        })),
      ),
      auto_invoice: Number(autoInvoiceNumber) || 0,
      is_auto_invoice_enabled: autoInvoiceEnabled
        ? IS_AUTO_INVOICE.enable
        : IS_AUTO_INVOICE.disable,
      prefix: prefix,
      is_barcode_enabled: isBardCodeEnabled ? IS_ACCESS.Yes : IS_ACCESS.No,
      is_dealer_code_enabled: isDealerCodeEnabled
        ? IS_ACCESS.Yes
        : IS_ACCESS.No,
      delivery_type: deliveryType,
    };
    updatePermissionSettingsService(data)
      .then((res) => {
        toast.success(res.data?.msg || "Settings updated successfully");
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.detail?.[0]?.msg || err.message;
        toast.error(errMsg);
      })
      .finally(() => {
        setisLoading(false);
      });
  };

  const handleSubMenuToggle = (mainMenuId: number, subMenuId: number) => {
    setPermissions((prev) =>
      prev.map((menu) => {
        if (menu.main_menu_id !== mainMenuId) return menu;
        return {
          ...menu,
          sub_menu: menu.sub_menu.map((sub) => {
            if (sub.sub_menu_id !== subMenuId) return sub;
            return {
              ...sub,
              access:
                sub.access === IS_ACCESS.Yes ? IS_ACCESS.No : IS_ACCESS.Yes,
            };
          }),
        };
      }),
    );
  };

  const handleAutoInvoiceNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const val = e.target.value;
    if (val === "" || (/^\d+$/.test(val) && val[0] !== "0")) {
      setAutoInvoiceNumber(val);
    }
  };

  const handleSave = () => {
    if (!autoInvoiceEnabled) {
      if (prefix.trim() && !autoInvoiceNumber) {
        toast.error("Please enter an auto invoice number");
        return;
      }
      if (autoInvoiceNumber && !prefix.trim()) {
        toast.error("Please enter a prefix");
        return;
      }
    }
    setisOpenConfirmModal(true);
  };

  const isInputEditable = !autoInvoiceEnabled;

  return (
    <div className={Styles.container}>
      <Loader isLoader={isLoading} />

      {isOpenOtpPopup && (
        <ExportOtpVerify
          show={isOpenOtpPopup}
          onClose={() => setisOpenOtpPopup(false)}
          onSuccessVerify={(resetKey: string) => {
            setisOpenOtpPopup(false);
            saveSettingsApiCall(resetKey);
          }}
          reportType={4}
          title="Verify OTP to Update Settings"
        />
      )}

      <Confirmation
        isOpen={isOpenConfirmModal}
        onHide={() => setisOpenConfirmModal(false)}
        onSucess={() => {
          setisOpenConfirmModal(false);
          setisOpenOtpPopup(true);
        }}
        title="Update Settings"
        content="Updating settings requires OTP verification. Do you want to proceed?"
        isConfirmation={true}
        successBtn="Proceed"
      />

      <h5 className={Styles.pageTitle}>
        {" "}
        <p className="fontBold pt-3 pb-1 fs-4">
          Sett
          <span className="orange">ings</span>
        </p>
      </h5>

      {errorText ? (
        <div className={`alert alert-danger ${Styles.errorAlert}`}>
          {errorText}
        </div>
      ) : null}

      {/* Permissions Section */}
      {permissions.length > 0 && (
        <div className={Styles.section}>
          {permissions.map((menu) => (
            <div key={menu.main_menu_id} className={Styles.permissionGroup}>
              <p className={Styles.mainMenuTitle}>{menu.main_menu_name}</p>
              <div className={Styles.subMenuList}>
                {menu.sub_menu.map((sub) => (
                  <div key={sub.sub_menu_id} className={Styles.subMenuItem}>
                    <span className={Styles.subMenuLabel}>
                      {sub.sub_menu_name}
                    </span>
                    <div className="form-check form-switch mb-0">
                      <input
                        className={`form-check-input ${Styles.switchInput}`}
                        type="checkbox"
                        role="switch"
                        checked={sub.access === IS_ACCESS.Yes}
                        onChange={() =>
                          handleSubMenuToggle(
                            menu.main_menu_id,
                            sub.sub_menu_id,
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Auto Invoice Section */}
      {settingsData?.settings !== undefined && (
        <div className={Styles.section}>
          <div className={Styles.permissionGroup}>
            <p className={Styles.mainMenuTitle}>Barcode</p>
            <div className={Styles.subMenuList}>
              <div className={Styles.subMenuItem}>
                <span className={Styles.subMenuLabel}>Enable / Disable</span>
                <div className="form-check form-switch mb-0">
                  <input
                    className={`form-check-input ${Styles.switchInput}`}
                    type="checkbox"
                    role="switch"
                    checked={isBardCodeEnabled}
                    onChange={() => {
                      setisBardCodeEnabled((pre) => !pre);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={Styles.permissionGroup}>
            <p className={Styles.mainMenuTitle}>Delivery Type</p>
            <div className={Styles.subMenuList}>
              {DELIVERY_TYPE_OPTIONS.map((option) => (
                <div key={option.value} className={Styles.subMenuItem}>
                  <span className={Styles.subMenuLabel}>{option.label}</span>
                  <div className="form-check mb-0">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deliveryType"
                      checked={deliveryType === option.value}
                      onChange={() => setDeliveryType(option.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={Styles.permissionGroup}>
            <p className={Styles.mainMenuTitle}>Referal Mobile Number</p>
            <div className={Styles.subMenuList}>
              <div className={Styles.subMenuItem}>
                <span className={Styles.subMenuLabel}>Enable/Disable</span>
                <div className="form-check form-switch mb-0">
                  <input
                    className={`form-check-input ${Styles.switchInput}`}
                    type="checkbox"
                    role="switch"
                    checked={isDealerCodeEnabled}
                    onChange={() => {
                      setisDealerCodeEnabled((prev) => !prev);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={`${Styles.sectionHeader} d-flex align-items-center`}>
            <p
              className={`${Styles.mainMenuTitle} mb-0`}
              style={{ marginRight: "15px" }}
            >
              Auto Invoice Number
            </p>
            <div className="form-check form-switch mb-0">
              <input
                className={`form-check-input ${Styles.switchInput}`}
                type="checkbox"
                role="switch"
                checked={autoInvoiceEnabled}
                onChange={() => {
                  setAutoInvoiceEnabled((prev) => {
                    if (!prev) {
                      if (!prefix.trim()) {
                        toast.error("Please enter a prefix");

                        return prev;
                      }
                      if (autoInvoiceNumber.trim() === "") {
                        toast.error("Please enter an auto invoice number");
                        return prev;
                      }
                    }
                    return !prev;
                  });
                }}
              />
            </div>
          </div>

          <div className={Styles.invoiceRow}>
            <label className={Styles.invoiceLabel}>Invoice Number</label>
            <div className={Styles.invoiceInputs}>
              <input
                type="text"
                className={`form-control ${Styles.invoiceInput}`}
                placeholder="Prefix"
                value={prefix}
                disabled={!isInputEditable}
                onChange={(e) => setPrefix(e.target.value)}
              />
              <input
                type="text"
                inputMode="numeric"
                className={`form-control ${Styles.invoiceInput}`}
                placeholder="Auto Invoice Number"
                value={autoInvoiceNumber}
                disabled={!isInputEditable}
                onChange={handleAutoInvoiceNumberChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className={Styles.saveContainer}>
        <button
          className={Styles.saveBtn}
          onClick={handleSave}
          disabled={isLoading}
        >
          Save
        </button>
      </div>
    </div>
  );
}
